/**
 * Configuration des filtres pour la page Facturation
 *
 * Définit les filtres disponibles pour les onglets externe et interne
 */

import { Eye, EyeOff } from 'lucide-react';

/**
 * Génère la configuration des mois pour le multiselect
 */
const MOIS_OPTIONS = [...Array(12)].map((_, i) => ({
  value: i + 1,
  label: new Date(2000, i).toLocaleString('fr-FR', { month: 'long' })
}));

/**
 * Configuration des trimestres
 */
const TRIMESTRE_OPTIONS = [
  { value: 'Q1', label: 'Q1' },
  { value: 'Q2', label: 'Q2' },
  { value: 'Q3', label: 'Q3' },
  { value: 'Q4', label: 'Q4' }
];

/**
 * Génère les filtres pour les factures externes
 *
 * @param {Object} props
 * @param {Array} props.anneesDisponibles - Liste des années disponibles
 * @param {number} props.filterAnnee - Année sélectionnée
 * @param {Function} props.setFilterAnnee - Setter pour l'année
 * @param {Array} props.filterMois - Mois sélectionnés
 * @param {Function} props.setFilterMois - Setter pour les mois
 * @param {Array} props.filterPrestataire - Prestataires sélectionnés
 * @param {Function} props.setFilterPrestataire - Setter pour les prestataires
 * @param {Array} props.prestatairesDisponibles - Liste des prestataires disponibles
 * @param {Array} props.filterStatut - Statuts sélectionnés
 * @param {Function} props.setFilterStatut - Setter pour les statuts
 * @param {Array} props.STATUTS - Liste des statuts possibles
 * @param {boolean} props.hidePaid - État du toggle masquer payées
 * @param {Function} props.setHidePaid - Setter pour masquer payées
 * @returns {Array} Configuration des filtres
 */
export function getExterneFilters(props) {
  const {
    anneesDisponibles,
    filterAnnee,
    setFilterAnnee,
    filterMois,
    setFilterMois,
    filterPrestataire,
    setFilterPrestataire,
    prestatairesDisponibles,
    filterStatut,
    setFilterStatut,
    STATUTS,
    hidePaid,
    setHidePaid
  } = props;

  return [
    // Filtre Année
    {
      type: 'select',
      value: filterAnnee,
      onChange: setFilterAnnee,
      options: anneesDisponibles.map(a => ({ value: a, label: a.toString() })),
      placeholder: 'Toutes les années',
      allowNull: true
    },
    // Filtre Mois (multiselect)
    {
      type: 'multiselect',
      value: filterMois,
      onChange: setFilterMois,
      options: MOIS_OPTIONS,
      placeholder: 'Tous les mois',
      formatDisplay: (values, options) => {
        if (values.length === 0) return 'Tous les mois';
        if (values.length === 1) {
          const opt = options.find(o => o.value === values[0]);
          return opt ? opt.label : 'Tous les mois';
        }
        return `${values.length} mois`;
      },
      capitalizeLabels: true,
      minWidth: '180px'
    },
    // Filtre Prestataire (multiselect)
    {
      type: 'multiselect',
      value: filterPrestataire,
      onChange: setFilterPrestataire,
      options: prestatairesDisponibles.map(p => ({
        value: p.id,
        label: p.nom
      })),
      placeholder: 'Tous les prestataires',
      formatDisplay: (values, options) => {
        if (values.length === 0) return 'Tous les prestataires';
        if (values.length === 1) {
          const opt = options.find(o => o.value === values[0]);
          return opt ? opt.label : '1 prestataire';
        }
        return `${values.length} prestataires`;
      },
      minWidth: '180px'
    },
    // Filtre Statut (multiselect)
    {
      type: 'multiselect',
      value: filterStatut,
      onChange: setFilterStatut,
      options: STATUTS.map(s => ({ value: s, label: s })),
      placeholder: 'Tous les statuts',
      formatDisplay: (values) => {
        if (values.length === 0) return 'Tous les statuts';
        if (values.length === 1) return values[0];
        return `${values.length} statuts`;
      },
      minWidth: '180px'
    },
    // Toggle Masquer payées
    {
      type: 'toggle',
      value: hidePaid,
      onChange: setHidePaid,
      labelOn: 'Payées masquées',
      labelOff: 'Masquer payées',
      iconOn: EyeOff,
      iconOff: Eye
    }
  ];
}

/**
 * Génère les filtres pour les factures internes
 *
 * @param {Object} props
 * @param {Array} props.anneesDisponibles - Liste des années disponibles
 * @param {number} props.filterAnnee - Année sélectionnée
 * @param {Function} props.setFilterAnnee - Setter pour l'année
 * @param {string} props.filterTrimestre - Trimestre sélectionné
 * @param {Function} props.setFilterTrimestre - Setter pour le trimestre
 * @param {string} props.filterConsultant - Consultant sélectionné
 * @param {Function} props.setFilterConsultant - Setter pour le consultant
 * @param {Array} props.consultantsDisponibles - Liste des consultants disponibles
 * @param {Array} props.filterStatut - Statuts sélectionnés
 * @param {Function} props.setFilterStatut - Setter pour les statuts
 * @param {Array} props.STATUTS - Liste des statuts possibles
 * @param {boolean} props.hidePaid - État du toggle masquer payées
 * @param {Function} props.setHidePaid - Setter pour masquer payées
 * @returns {Array} Configuration des filtres
 */
export function getInterneFilters(props) {
  const {
    anneesDisponibles,
    filterAnnee,
    setFilterAnnee,
    filterTrimestre,
    setFilterTrimestre,
    filterConsultant,
    setFilterConsultant,
    consultantsDisponibles,
    filterStatut,
    setFilterStatut,
    STATUTS,
    hidePaid,
    setHidePaid
  } = props;

  return [
    // Filtre Année
    {
      type: 'select',
      value: filterAnnee,
      onChange: setFilterAnnee,
      options: anneesDisponibles.map(a => ({ value: a, label: a.toString() })),
      placeholder: 'Toutes les années',
      allowNull: true
    },
    // Filtre Trimestre
    {
      type: 'select',
      value: filterTrimestre,
      onChange: setFilterTrimestre,
      options: TRIMESTRE_OPTIONS,
      placeholder: 'Tous les trimestres',
      allowNull: true
    },
    // Filtre Consultant
    {
      type: 'select',
      value: filterConsultant,
      onChange: setFilterConsultant,
      options: consultantsDisponibles.map(c => ({
        value: c.id,
        label: c.nom
      })),
      placeholder: 'Tous les consultants',
      allowNull: true
    },
    // Filtre Statut (multiselect)
    {
      type: 'multiselect',
      value: filterStatut,
      onChange: setFilterStatut,
      options: STATUTS.map(s => ({ value: s, label: s })),
      placeholder: 'Tous les statuts',
      formatDisplay: (values) => {
        if (values.length === 0) return 'Tous les statuts';
        if (values.length === 1) return values[0];
        return `${values.length} statuts`;
      },
      minWidth: '180px'
    },
    // Toggle Masquer payées
    {
      type: 'toggle',
      value: hidePaid,
      onChange: setHidePaid,
      labelOn: 'Payées masquées',
      labelOff: 'Masquer payées',
      iconOn: EyeOff,
      iconOff: Eye
    }
  ];
}
